import { useState, type ReactElement } from 'react';
import { toast } from '../../../design-system/components';
import { apiErrorCode, emberApi, type AnswererQuestion, type AskerQuestion, type Knock, type QuestionAnswer, type QuestionRow, type QuestionStance } from '../api';
import { ApiError } from '../../auth/api-client';
import { trackFailed } from '../../../../../shared-web/track';
import { letterState } from './knock-letters';
import { navigate } from '../router';
import type { KnockCardFrame } from './KnockCard';
import './KnockCard.css';

/**
 * Ask people (v4.0 §4.19, 2026-09-20 拍板) — the question on the paper, both
 * sides, in the knock card's clothes.
 *
 * To an answerer: a question drafted from someone else's conversation with
 * Ember, with two or three sentences of context, nothing of who they are.
 * They answer in their own words, name the stance they answer from (lived it
 * / seen it up close / just a perspective) or none, or leave it for others.
 * The other answers so far are on the same page — the one place another
 * person's words are readable here, and only on a question sent to you.
 *
 * To the asker: the question as it went out, and every answer as it came
 * back, in the answerers' own words. Ember reads the same answers in the
 * conversation; this page is where they are kept.
 *
 * Every answer shown to a person carries `Report` (2026-09-23 拍板, on
 * counsel's content-safety note): one tap hides it for them at once and puts
 * it in front of an operator, who can take it down for everyone. The words
 * were read once by a gate before anyone saw them (a way to be reached,
 * something aimed at a person); a refused answer comes back with why, and the
 * person edits — nothing is ever rewritten for them.
 */
export function QuestionCard({ row, onChange, frame = 'card' }: {
  row: QuestionRow;
  onChange?: (next: Knock) => void;
  frame?: KnockCardFrame;
}): ReactElement {
  return (
    <section
      className={`knock-card is-question is-${row.role}${frame === 'letter' ? ' is-letter' : ''}`}
      aria-label={row.role === 'asker' ? 'The question Ember carried for you' : 'A question for you'}
      data-log-seen="knock_card"
      data-log-state={letterState(row)}
      data-log-match-id={row.match_id}
    >
      {row.role === 'asker' ? <AskerSide row={row} onChange={onChange} /> : <AnswererSide row={row} onChange={onChange} />}
      {frame === 'letter' ? (
        <p className="question-page-door">
          <button type="button" className="knock-more-door" data-log="question_page_open" data-log-state={row.role} data-log-match-id={row.match_id} onClick={() => navigate(`/q/${encodeURIComponent(row.question.id)}`)}>
            Open the question page
          </button>
        </p>
      ) : null}
    </section>
  );
}

const STANCES: { key: QuestionStance; label: string }[] = [
  { key: 'lived', label: 'I’ve lived something like this' },
  { key: 'seen', label: 'I’ve seen it up close' },
  { key: 'perspective', label: 'Just my perspective' },
];

const stanceLabel = (s: QuestionStance | null): string => STANCES.find((x) => x.key === s)?.label ?? '';

function Question({ q }: { q: QuestionRow['question'] }): ReactElement {
  return (
    <>
      <blockquote className="knock-situation knock-situation-question">
        <p dir="auto">{q.text}</p>
      </blockquote>
      {q.context ? <p className="knock-lede question-context" dir="auto">{q.context}</p> : null}
    </>
  );
}

function Answers({ answers, heading, onMattered, onReport, reported }: {
  answers: QuestionAnswer[];
  heading: string;
  onMattered?: (a: QuestionAnswer) => Promise<void>;
  onReport: (a: QuestionAnswer) => Promise<void>;
  reported: Set<string>;
}): ReactElement | null {
  // A report hides the answer for this person for good, so the link asks once (a slip of the thumb should not lose an answer).
  const [confirming, setConfirming] = useState<string | null>(null);
  if (answers.length === 0) return null;
  return (
    <div className="question-answers">
      <p className="knock-lede question-answers-head">{heading}</p>
      {answers.map((a) => (
        <div key={a.id} className={`question-answer${a.seen === false ? ' is-new' : ''}`}>
          {reported.has(a.id) ? (
            <p className="question-answer-reported">Reported. It’s hidden for you, and someone will look at it.</p>
          ) : (
            <>
              <p className="question-answer-text" dir="auto">{a.text}</p>
              {a.stance ? <p className="question-answer-stance">{stanceLabel(a.stance)}</p> : null}
              <div className="question-answer-doors">
                {onMattered ? (
                  a.mattered
                    ? <p className="question-answer-mattered">You told them it mattered.</p>
                    : <button type="button" className="knock-more-door question-mattered" data-log="question_mattered" onClick={() => { void onMattered(a); }}>This mattered</button>
                ) : null}
                {confirming === a.id ? (
                  <span className="question-report-confirm">
                    Hide this answer and flag it?
                    <button type="button" className="question-report" data-log="question_report" onClick={() => { setConfirming(null); void onReport(a); }}>Yes</button>
                    <button type="button" className="question-report" onClick={() => setConfirming(null)}>No</button>
                  </span>
                ) : (
                  <button type="button" className="question-report" onClick={() => setConfirming(a.id)}>Report</button>
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/** One tap: the answer is hidden for this person now, and an operator reads it. */
function useReport(row: QuestionRow): { reported: Set<string>; report: (a: QuestionAnswer) => Promise<void> } {
  const [reported, setReported] = useState<Set<string>>(() => new Set());
  const report = async (a: QuestionAnswer): Promise<void> => {
    try {
      await emberApi.reportAnswer(row.question.id, a.id);
      setReported((cur) => new Set(cur).add(a.id));
    } catch (err) {
      toast.error("Couldn't send that. Try again.");
      trackFailed('question_report', apiErrorCode(err) || 'error', { match_id: row.match_id });
    }
  };
  return { reported, report };
}

function AskerSide({ row, onChange }: { row: AskerQuestion; onChange?: (next: Knock) => void }): ReactElement {
  const n = row.answers.length;
  const [busy, setBusy] = useState(false);
  const { reported, report } = useReport(row);
  const mattered = async (a: QuestionAnswer): Promise<void> => {
    try {
      await emberApi.markAnswerMattered(row.question.id, a.id);
      onChange?.({ ...row, answers: row.answers.map((x) => (x.id === a.id ? { ...x, mattered: true } : x)) });
    } catch (err) {
      toast.error("Couldn't save that. Try again.");
      trackFailed('question_mattered', apiErrorCode(err) || 'error', { match_id: row.match_id });
    }
  };
  const withdraw = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await emberApi.withdrawQuestion(row.question.id);
      onChange?.({ ...row, letter: null, question: { ...row.question, withdrawn_at: r.withdrawn_at } });
    } catch (err) {
      toast.error("Couldn't save that. Try again.");
      trackFailed('question_withdraw', apiErrorCode(err) || 'error', { match_id: row.match_id });
    } finally {
      setBusy(false);
    }
  };
  // Ended: the asker withdrew it, or Ember closed it (2026-10-07).
  const ended = row.question.withdrawn_at ? 'Withdrawn' : row.question.status === 'closed' ? 'Closed' : null;
  return (
    <>
      <p className="knock-lede">The question, as it went out{row.question.invited ? ` to ${row.question.invited} people` : ''}:</p>
      <Question q={row.question} />
      {n === 0
        ? <p className="knock-anytime">{ended ? `${ended}. No one new will be asked.` : 'No answers yet. I’ll let you know when someone writes back.'}</p>
        : <Answers answers={row.answers} heading={n === 1 ? 'What they said:' : 'What they said, in their own words:'} onMattered={mattered} onReport={report} reported={reported} />}
      {ended
        ? (n > 0 ? <p className="knock-anytime">{ended}. No one new will be asked; what came back stays here.</p> : null)
        : (
          <p className="question-withdraw">
            <button type="button" className="knock-more-door" disabled={busy} data-log="question_withdraw" onClick={() => { void withdraw(); }}>
              Withdraw the question
            </button>
            <span className="question-withdraw-note"> — no one new is asked; what has come back stays.</span>
          </p>
        )}
    </>
  );
}

function AnswererSide({ row, onChange }: { row: AnswererQuestion; onChange?: (next: Knock) => void }): ReactElement {
  const [text, setText] = useState('');
  const [stance, setStance] = useState<QuestionStance | null>(null);
  const [busy, setBusy] = useState<null | 'send' | 'pass'>(null);
  const [problem, setProblem] = useState('');
  const { reported, report } = useReport(row);
  const trimmed = text.trim();

  const send = async (): Promise<void> => {
    if (busy || !trimmed) return;
    setBusy('send');
    setProblem('');
    try {
      const r = await emberApi.answerQuestion(row.question.id, { text: trimmed, stance });
      onChange?.({ ...row, letter: null, mine: r.answer });
    } catch (err) {
      const code = apiErrorCode(err);
      // The gate refused the words (2026-09-23): the text stays in the field, the reason is said, and they edit.
      if (code === 'text_rejected') {
        setProblem('Part of this reads as aimed at a person, or as something we can’t pass along. Tell it as your own experience and send it again.');
      } else if (code === 'moderation_unavailable') {
        setProblem('Ember couldn’t look this over just now. Your words are still here — try again in a moment.');
      } else if (code === 'risk_to_self') {
        // The answer carried a risk to the person themselves (2026-10-01): it stays theirs, unsent, with the crisis resources in front of them — the room line's door.
        const resources = (err as ApiError).body && typeof (err as ApiError).body === 'object' ? String(((err as ApiError).body as { resources?: string }).resources || '') : '';
        setProblem(`This answer stays with you — it isn’t passed along. ${resources}`.trim());
      } else {
        toast.error("Couldn't send that. Try again.");
      }
      trackFailed('question_reply', code || 'error', { match_id: row.match_id });
    } finally {
      setBusy(null);
    }
  };
  const pass = async (): Promise<void> => {
    if (busy) return;
    setBusy('pass');
    try {
      await emberApi.passQuestion(row.question.id);
      onChange?.({ ...row, letter: null, passed: true });
    } catch (err) {
      toast.error("Couldn't save that. Try again.");
      trackFailed('question_pass', apiErrorCode(err) || 'error', { match_id: row.match_id });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Question q={row.question} />
      {row.mine ? (
        <div className="question-answers">
          <p className="knock-lede question-answers-head">Your answer:</p>
          <div className="question-answer is-mine">
            <p className="question-answer-text" dir="auto">{row.mine.text}</p>
            {row.mine.stance ? <p className="question-answer-stance">{stanceLabel(row.mine.stance)}</p> : null}
            {row.mine.mattered ? <p className="question-answer-mattered">They wanted you to know: it mattered.</p> : null}
          </div>
          {row.mine.removed
            ? <p className="knock-anytime">This answer was reported and taken down, so it’s no longer shown to anyone but you.</p>
            : !row.mine.mattered ? <p className="knock-anytime">It’s with them now. Thank you.</p> : null}
        </div>
      ) : row.passed ? (
        <p className="knock-anytime">Left for others. Nothing more is asked of you.</p>
      ) : row.question.withdrawn_at ? (
        <p className="knock-anytime">They withdrew the question. Nothing more is asked of you.</p>
      ) : row.question.status === 'closed' ? (
        <p className="knock-anytime">This question has closed. Nothing more is asked of you.</p>
      ) : (
        <form className="knock-line question-form" onSubmit={(e) => { e.preventDefault(); void send(); }}>
          <p className="knock-lede">If you have stood somewhere near this, what would you tell them? Your words go to them, and to the few others asked, as you write them; no one learns who you are.</p>
          <textarea
            className="knock-line-field"
            placeholder="In your own words…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            maxLength={2000}
            dir="auto"
            aria-label="Your answer"
          />
          <div className="question-stances" role="group" aria-label="You’re answering from">
            {STANCES.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`question-stance${stance === s.key ? ' is-on' : ''}`}
                aria-pressed={stance === s.key}
                onClick={() => setStance((cur) => (cur === s.key ? null : s.key))}
              >
                {s.label}
              </button>
            ))}
          </div>
          {problem ? <p className="question-problem" role="alert">{problem}</p> : null}
          <div className="knock-actions">
            <button
              type="submit"
              className={`ember-btn ember-btn-primary${busy === 'send' ? ' is-busy' : ''}`}
              disabled={busy !== null || !trimmed}
              data-log="question_reply"
              data-log-state={stance ?? 'none'}
              data-log-match-id={row.match_id}
            >
              Send
            </button>
            <button
              type="button"
              className="ember-btn ember-btn-secondary"
              disabled={busy !== null}
              data-log="question_pass"
              data-log-match-id={row.match_id}
              onClick={() => { void pass(); }}
            >
              Not this one
            </button>
          </div>
        </form>
      )}
      <Answers answers={row.answers} heading={row.answers.length === 1 ? 'What someone else said:' : 'What others have said:'} onReport={report} reported={reported} />
    </>
  );
}
